import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { EvaluationContext } from "../stores/EvaluationContext";
import { CatalogContext } from "../stores/CatalogContext";
import { getChapterProgress } from "../utils/completionProgress";
import {
  ACR_EDITOR_ABOUT_URL,
  ACR_EDITOR_CHAPTER_URL,
  ACR_EDITOR_REPORT_URL,
} from "../../../utlities/core.constants";
import "./ReportProgressCard.css";

function ProgressBar({ filled, total, label }) {
  const pct = total > 0 ? Math.round((filled / total) * 100) : 0;
  return (
    <div
      className="rpc-progress-track"
      role="progressbar"
      aria-label={label}
      aria-valuenow={filled}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      <div className="rpc-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

ProgressBar.propTypes = {
  filled: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

function ChapterGroupSection({ group, chapterProgressList, navigate }) {
  const groupChapters = chapterProgressList.filter((ch) =>
    group.chapters.some((gc) => gc.id === ch.id)
  );
  const groupFilled = groupChapters.reduce(
    (sum, ch) => sum + ch.progress.filled,
    0
  );
  const groupTotal = groupChapters.reduce(
    (sum, ch) => sum + ch.progress.total,
    0
  );

  return (
    <details className="rpc-group" open>
      <summary className="rpc-group-summary">
        <span className="rpc-group-label">{group.label}</span>
        <span className="rpc-group-count">
          {groupFilled} of {groupTotal}
        </span>
      </summary>
      <ul className="rpc-chapter-list">
        {groupChapters.map((ch) => (
          <li key={ch.id} className="rpc-chapter-item">
            <button
              type="button"
              className="rpc-chapter-btn"
              onClick={() => navigate(`${ACR_EDITOR_CHAPTER_URL}/${ch.id}`)}
            >
              <span className="rpc-chapter-row">
                <span className="rpc-chapter-label">{ch.short_label}</span>
                <span className="rpc-chapter-count">
                  {ch.progress.status === "disabled"
                    ? "Disabled"
                    : `${ch.progress.filled} of ${ch.progress.total}`}
                </span>
              </span>
            </button>
            <ProgressBar
              filled={ch.progress.filled}
              total={ch.progress.total}
              label={`${ch.short_label}: ${ch.progress.filled} of ${ch.progress.total} criteria reported`}
            />
          </li>
        ))}
      </ul>
    </details>
  );
}

ChapterGroupSection.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    chapters: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string.isRequired })
    ).isRequired,
  }).isRequired,
  chapterProgressList: PropTypes.arrayOf(PropTypes.object).isRequired,
  navigate: PropTypes.func.isRequired,
};

export default function ReportProgressCard({ chapterGroups }) {
  const { evaluation } = useContext(EvaluationContext);
  const { getCatalog } = useContext(CatalogContext);
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const showBtnRef = useRef(null);
  const hideBtnRef = useRef(null);
  const didToggleRef = useRef(false);

  // Move focus to the toggle button only when the user explicitly hides/shows — not on initial mount
  useEffect(() => {
    if (!didToggleRef.current) return;
    if (isVisible) {
      hideBtnRef.current?.focus();
    } else {
      showBtnRef.current?.focus();
    }
  }, [isVisible]);

  const catalog = useMemo(
    () => getCatalog(evaluation?.catalog),
    [getCatalog, evaluation?.catalog]
  );

  const chapters = catalog?.chapters ?? [];
  const hasReport = Boolean(evaluation?.catalog && chapters.length > 0);

  const chapterProgressList = useMemo(
    () =>
      chapters.map((ch) => ({
        ...ch,
        progress: getChapterProgress(ch.id, evaluation, ch),
      })),
    [chapters, evaluation]
  );

  const totalFilled = chapterProgressList.reduce(
    (sum, ch) => sum + ch.progress.filled,
    0
  );
  const totalCriteria = chapterProgressList.reduce(
    (sum, ch) => sum + ch.progress.total,
    0
  );

  // Use groups if available, otherwise fall back to a flat ungrouped list
  const useGroups = chapterGroups && chapterGroups.length > 0;

  if (!isVisible) {
    return (
      <aside
        className="report-progress-card report-progress-card--collapsed"
        aria-label="Your report"
      >
        <button
          ref={showBtnRef}
          type="button"
          className="rpc-show-btn"
          onClick={() => { didToggleRef.current = true; setIsVisible(true); }}
        >
          Show Your Report
        </button>
      </aside>
    );
  }

  return (
    <aside className="report-progress-card" aria-label="Your report">
      <div className="rpc-header">
        <h2 className="rpc-title">Your report</h2>
        <button
          ref={hideBtnRef}
          type="button"
          className="rpc-hide-btn"
          onClick={() => { didToggleRef.current = true; setIsVisible(false); }}
        >
          Hide <span aria-hidden="true">&rsaquo;</span>
        </button>
      </div>

      {!hasReport && (
        <>
          <p className="rpc-status">No report started.</p>
          <button
            type="button"
            className="rpc-btn-primary"
            onClick={() => navigate(ACR_EDITOR_ABOUT_URL)}
          >
            Start a new ACR
          </button>
        </>
      )}

      {hasReport && (
        <>
          <p className="rpc-total-summary">
            Reported on <strong>{totalFilled}</strong> of{" "}
            <strong>{totalCriteria}</strong> Total Criteria.
          </p>
          <ProgressBar
            filled={totalFilled}
            total={totalCriteria}
            label={`Overall progress: ${totalFilled} of ${totalCriteria} criteria reported`}
          />

          {useGroups ? (
            <div className="rpc-groups">
              {chapterGroups.map((group) => (
                <ChapterGroupSection
                  key={group.id}
                  group={group}
                  chapterProgressList={chapterProgressList}
                  navigate={navigate}
                />
              ))}
            </div>
          ) : (
            <ul className="rpc-chapter-list" aria-label="Chapter progress">
              {chapterProgressList.map((ch) => (
                <li key={ch.id} className="rpc-chapter-item">
                  <button
                    type="button"
                    className="rpc-chapter-btn"
                    onClick={() =>
                      navigate(`${ACR_EDITOR_CHAPTER_URL}/${ch.id}`)
                    }
                  >
                    <span className="rpc-chapter-row">
                      <span className="rpc-chapter-label">
                        {ch.short_label}
                      </span>
                      <span className="rpc-chapter-count">
                        {ch.progress.status === "disabled"
                          ? "Disabled"
                          : `${ch.progress.filled} of ${ch.progress.total}`}
                      </span>
                    </span>
                  </button>
                  <ProgressBar
                    filled={ch.progress.filled}
                    total={ch.progress.total}
                    label={`${ch.short_label}: ${ch.progress.filled} of ${ch.progress.total} criteria reported`}
                  />
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            className="rpc-btn-primary"
            onClick={() => navigate(ACR_EDITOR_REPORT_URL)}
          >
            View Report
          </button>
        </>
      )}
    </aside>
  );
}

ReportProgressCard.propTypes = {
  chapterGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      chapters: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.string.isRequired })
      ).isRequired,
    })
  ),
};

ReportProgressCard.defaultProps = {
  chapterGroups: [],
};
